import { isBillingDev, mergeDeep } from '../../common/ambient'

const fetch = require('isomorphic-fetch')

/// ////////////////////////////////////////////////////////////////////////////
// SET THE BILLING SERVER HERE
// Note, the AWN backend MUST match.  Rainmaker requires lightning.  Test server requires tornado (search for it)
// Note, the Stripe publishable key MUST match.  Use pk_test_ or pk_live_.  Search for PUBLISHABLE_KEY.
// const billingServer = "https://rainmaker.ambientweather.net"  // Production server
const billingServer = isBillingDev() ? 'https://ambient-weather-billing-test-z49ii.ondigitalocean.app' : 'https://rainmaker.ambientweather.net'
/// ////////////////////////////////////////////////////////////////////////////

/**
 * Base fetch function for all billing API calls
 * @param {string} path - API endpoint path
 * @param {Object} options - Fetch options
 * @param {boolean} returnFullResponse - If true, returns the full response object instead of just JSON
 * @returns {Promise<Object>} - Response data or full response object
 */
async function _fetchBillingApi(path, options = {}, returnFullResponse = false) {
  const url = billingServer + path
  const jwt = localStorage.getItem('feathers-jwt')
  
  try {
    const defaultOptions = {
      headers: {
        Authorization: `Bearer ${jwt}`
      }
    }
    
    // Merge default options with provided options
    const fetchOptions = mergeDeep(defaultOptions, options)
    
    const response = await fetch(url, fetchOptions)
    
    if (returnFullResponse) {
      return response
    }
    
    return response.json()
  } catch (error) {
    _handleError(error)
    return returnFullResponse ? null : {}
  }
}

export async function fetchFromBilling(path, fetchObj) {
  return _fetchBillingApi(path, fetchObj)
}

async function _getFromBilling(path, options = {}) {
  return _fetchBillingApi(path, options, true)
}

function _handleError(error) {
  // console.log('###________________________________###')
  // console.log(error)
  // console.log('###________________________________###')
}

/**
 * Helper function to check if response was successful (status 200-299)
 * @param {Response} response - Fetch response object
 * @returns {boolean} - Whether the response was successful
 */
function isSuccessResponse(response) {
  return response && response.status >= 200 && response.status <= 299
}

/// ////////////////////////////////////////////////////////////////////////////
//
export async function billingFetchSecret(userId, setSecretTo) {
  try {
    const path = `/users/${userId}/card-setup`
    const clientSecret = await _fetchBillingApi(path)
    
    if (clientSecret && clientSecret.clientSecret) {
      setSecretTo(clientSecret.clientSecret)
    }
  } catch (error) {
    _handleError(error)
  }
}

/// ////////////////////////////////////////////////////////////////////////////
// GET /users/:userId/cards
export async function billingGetUserCards(userId, setUserCards) {
  try {
    const path = `/users/${userId}/cards`
    const response = await _getFromBilling(path)
    
    if (!response) return false
    
    const userCards = await response.json()
    setUserCards(userCards)
    return isSuccessResponse(response)
  } catch (error) {
    _handleError(error)
    return false
  }
}

/// ////////////////////////////////////////////////////////////////////////////
//  GET /subscriptions?userId=someUserId
//  GET /subscriptions?userId=someUserId&productId=someProductId (optional)
export async function billingGetUserSubs(userId, setUserSubs) {
  try {
    const path = `/subscriptions?userId=${userId}`
    const response = await _getFromBilling(path)
    
    if (!response) return false
    
    const userSubs = await response.json()
    setUserSubs(userSubs)
    return isSuccessResponse(response)
  } catch (error) {
    _handleError(error)
    return false
  }
}

/// ////////////////////////////////////////////////////////////////////////////
// PUT /subscriptions?userId=${userId}&macAddress=${macAddress}&productName=${productName}

export async function billingSetAutorenew(userId, macAddress, productName, renew) {
  try {
    const path = `/subscriptions?userId=${userId}&macAddress=${macAddress}&productName=${productName}`
    const cancel_at_period_end = !renew
    
    const options = {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ cancel_at_period_end })
    }
    
    const response = await _getFromBilling(path, options)
    return isSuccessResponse(response)
  } catch (error) {
    _handleError(error)
    return false
  }
}

export async function billingFetch(path, opts = {}) {
  try {
    const options = {
      method: opts.method || 'GET'
    }
    
    const response = await _getFromBilling(path, options)
    
    if (!response) return false
    
    const data = await response.json()
    return isSuccessResponse(response) ? data : false
  } catch (error) {
    _handleError(error)
    return false
  }
}

export async function checkCode(code) {
  return _fetchBillingApi(`/promo-codes/${code}`)
}

/// ////////////////////////////////////////////////////////////////////////////
// POST /subscriptions?userId=${userId}&macAddress=${macAddress}&productName=${productName}
export async function billingSubscribe(userId, macAddress, productName, opts = {}) {
  try {
    let queryParams = `/subscriptions?userId=${userId}`

    // Only add macAddress if it's provided (not null or undefined)
    if (macAddress !== null && macAddress !== undefined) {
      queryParams += `&macAddress=${macAddress}`
    }

    queryParams += `&productName=${productName}`

    if (opts.promoCode) {
      queryParams += `&promoCode=${opts.promoCode}`
    }

    if (opts.discountPercent) {
      queryParams += `&discountPercent=${opts.discountPercent}`
    }

    if (opts.isPreview) {
      queryParams += `&isPreview=true`
    }

    const options = {
      method: opts.method || 'POST',
      headers: {
        ...opts.headers
      }
    }

    if (opts.body) {
      options.body = JSON.stringify(opts.body)
      options.headers['Content-Type'] = 'application/json'
    }

    const response = await _getFromBilling(queryParams, options)

    // If response is not successful, try to get error message
    if (!isSuccessResponse(response)) {
      try {
        const errorData = await response.json()
        return { success: false, message: errorData.message || 'An error occurred' }
      } catch {
        return { success: false, message: 'An error occurred' }
      }
    }

    // For preview requests, return the full response data
    if (opts.isPreview) {
      const previewData = await response.json()
      return { success: true, ...previewData }
    }

    return { success: true }
  } catch (error) {
    _handleError(error)
    return { success: false, message: 'Network error occurred' }
  }
}

/// ////////////////////////////////////////////////////////////////////////////
// POST http://servername/users/:userId/cards/:paymentId/default (empty body)
export async function billingMakeCardDefault(userId, paymentId) {
  try {
    const path = `/users/${userId}/cards/${paymentId}/default`
    const options = {
      method: 'POST'
    }
    
    const response = await _getFromBilling(path, options)
    return isSuccessResponse(response)
  } catch (error) {
    _handleError(error)
    return false
  }
}

/// ////////////////////////////////////////////////////////////////////////////
// DELETE /users/:userId/cards/:cardId
export async function billingDeleteCard(userId, paymentId) {
  try {
    const path = `/users/${userId}/cards/${paymentId}`
    const options = {
      method: 'DELETE'
    }
    
    const response = await _getFromBilling(path, options)
    return isSuccessResponse(response)
  } catch (error) {
    _handleError(error)
    return false
  }
}

/**
 * Updates a user's email address on the billing server
 * @param {string} userId - The user's ID
 * @param {string} newEmail - The new email address
 * @returns {Promise<Object>} - Result with success status and any response data
 */
export async function billingUpdateUserEmail(userId, newEmail) {
  try {
    const path = `/users/${userId}/email`
    const options = {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ newEmail })
    }
    
    const response = await _getFromBilling(path, options)
    
    if (!response) return { success: false }
    
    // Get the JSON response data
    const responseData = await response.json()
    
    return {
      success: isSuccessResponse(response),
      message: responseData.message || '',
      data: responseData
    }
  } catch (error) {
    _handleError(error)
    return { success: false, message: error.message || 'Unknown error occurred' }
  }
}

/// ////////////////////////////////////////////////////////////////////////////
// POST /subscriptions/apply-promo-code/?userId={userId}&promoCode={code}&isPreview={true/false}
/**
 * Apply a promo code to an existing subscription
 * @param {string} userId - The user's ID
 * @param {string} promoCode - The promo code to apply
 * @param {boolean} isPreview - If true, returns preview data without applying the code
 * @returns {Promise<Object>} - Result with success status and preview/response data
 */
export async function billingApplyPromoCode(userId, promoCode, isPreview = false) {
  try {
    let path = `/subscriptions/apply-promo-code/?userId=${userId}&promoCode=${promoCode}`

    if (isPreview) {
      path += '&isPreview=true'
    }

    const options = {
      method: 'POST'
    }

    const response = await _getFromBilling(path, options)

    if (!isSuccessResponse(response)) {
      const errorData = await response.json().catch(() => ({}))
      return { success: false, message: errorData.message || 'An error occurred' }
    }

    // For preview, return full data
    if (isPreview) {
      const previewData = await response.json()
      return { success: true, ...previewData }
    }

    return { success: true }
  } catch (error) {
    _handleError(error)
    return { success: false, message: 'Network error occurred' }
  }
}

/// ////////////////////////////////////////////////////////////////////////////
// POST /subscriptions/defer?email=email&extendByDays=days&productName=productName&macAddress=macAddress
/**
 * Defer a subscription by extending it for a specified number of days
 * @param {string} email - User's email
 * @param {number} extendByDays - Number of days to extend the subscription
 * @param {string} productName - Product name (e.g. "awn-plus-account-monthly")
 * @param {string} [macAddress] - Optional MAC address for device-level subscriptions
 * @returns {Promise<boolean>} - Whether the operation was successful
 */
export async function billingDeferSubscription(email, extendByDays, productName, macAddress) {
  try {
    let path = `/subscriptions/defer?email=${encodeURIComponent(email)}&extendByDays=${extendByDays}&productName=${encodeURIComponent(productName)}`

    // Only add macAddress if it's provided (not null or undefined)
    if (macAddress !== null && macAddress !== undefined) {
      path += `&macAddress=${encodeURIComponent(macAddress)}`
    }
    const options = {
      method: 'POST'
    }

    const response = await _getFromBilling(path, options)

    if (!response) return { success: false }

    // Get the JSON response data
    const responseData = await response.json()

    return {
      success: isSuccessResponse(response),
      message: responseData.message || '',
      data: responseData
    }
  } catch (error) {
    _handleError(error)
    return { success: false, message: error.message || 'Unknown error occurred' }
  }
}
